
import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

export interface User {
  id: string;
  email: string;
  created_at: string;
  updated_at: string;
  subscription_id: string;
  email_confirmed_at?: string;
}

export type SortColumn = "email" | "created_at" | "updated_at";
export type SortDirection = "asc" | "desc";

export function useUserManagement() {
  const { user: currentUser, subscriptionData } = useAuth();
  const { toast } = useToast();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortColumn, setSortColumn] = useState<SortColumn>("email");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [filter, setFilter] = useState("");

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [currentUserEdit, setCurrentUserEdit] = useState<User | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (subscriptionData.subscription_id) {
      loadUsers();
    }
  }, [subscriptionData.subscription_id, sortColumn, sortDirection, filter]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      if (!subscriptionData.subscription_id) return;

      console.log("🔍 Carregando usuários para subscription:", subscriptionData.subscription_id);

      let query = supabase
        .from('profiles')
        .select('id, email, created_at, updated_at, subscription_id')
        .eq('subscription_id', subscriptionData.subscription_id);

      if (filter) {
        query = query.ilike('email', `%${filter}%`);
      }

      query = query.order(sortColumn, { ascending: sortDirection === "asc" });

      const { data, error } = await query;

      if (error) throw error;

      console.log("✅ Usuários carregados:", data?.length || 0);
      setUsers(data || []);
    } catch (error) {
      console.error("❌ Erro ao carregar usuários:", error);
      toast({
        title: "Erro ao carregar usuários",
        description: "Não foi possível carregar a lista de usuários.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const handleDelete = (user: User | null) => {
    setCurrentUserEdit(user);
    setDeleteDialogOpen(user !== null);
  };

  const handleCreateUser = async (email: string, password: string) => {
    if (!subscriptionData.subscription_id) {
      toast({
        title: "Erro de configuração",
        description: "Subscription ID não encontrado. Verifique sua conta.",
        variant: "destructive"
      });
      return;
    }
    
    setIsSaving(true);
    console.log("🚀 Iniciando criação de usuário:", email);
    console.log("📋 Subscription ID para associar:", subscriptionData.subscription_id);
    
    try {
      // Verificar se o usuário já existe
      const { data: existingUser } = await supabase
        .from('profiles')
        .select('email')
        .eq('email', email)
        .eq('subscription_id', subscriptionData.subscription_id)
        .single();

      if (existingUser) {
        throw new Error("Este email já está cadastrado na organização");
      }

      console.log("📧 Criando usuário com subscription_id nos metadados...");

      // Criar usuário com subscription_id nos metadados do usuário
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            subscription_id: subscriptionData.subscription_id
          }
        }
      });

      console.log("📊 Resultado do signUp:", { 
        user: !!authData.user, 
        session: !!authData.session,
        error: authError?.message 
      });

      if (authError) {
        if (authError.message.includes("User already registered")) {
          throw new Error("Este email já possui uma conta. Peça para o usuário fazer login diretamente.");
        }
        throw authError;
      }

      if (!authData.user) {
        throw new Error("Falha ao criar usuário no sistema de autenticação");
      }

      console.log("👤 Usuário criado com ID:", authData.user.id);

      // Aguardar um momento para o trigger criar o perfil
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Verificar se o perfil foi criado com o subscription_id correto
      console.log("🔍 Verificando se o perfil foi criado corretamente...");
      const { data: profileCheck, error: profileError } = await supabase
        .from('profiles')
        .select('id, email, subscription_id')
        .eq('id', authData.user.id)
        .single();

      console.log("📋 Perfil verificado:", profileCheck);

      // Se o perfil não foi criado ou não tem subscription_id, criar/atualizar manualmente
      if (!profileCheck || !profileCheck.subscription_id) {
        console.log("🔧 Corrigindo subscription_id no perfil...");
        
        const { error: updateError } = await supabase
          .from('profiles')
          .upsert({
            id: authData.user.id,
            email: email,
            subscription_id: subscriptionData.subscription_id,
            updated_at: new Date().toISOString()
          });

        if (updateError) {
          console.error("❌ Erro ao atualizar perfil:", updateError);
          throw new Error("Erro ao associar usuário à organização: " + updateError.message);
        }

        console.log("✅ Subscription_id associado manualmente ao perfil");
      }

      // Recarregar a lista de usuários
      setTimeout(async () => {
        await loadUsers();
      }, 1000);
      
      setCreateDialogOpen(false);
      
      // Verificar se o email foi enviado
      const emailSent = !authData.session && authData.user.email_confirmed_at === null;
      
      if (emailSent) {
        toast({
          title: "Convite enviado com sucesso",
          description: `Um email de confirmação foi enviado para ${email}. O usuário deve clicar no link para ativar a conta e será automaticamente associado à sua organização.`
        });
      } else {
        toast({
          title: "Usuário criado com sucesso",
          description: `Usuário ${email} foi criado e associado à sua organização.`
        });
      }

      console.log("✅ Processo de criação de usuário concluído com sucesso");

    } catch (error: any) {
      console.error("❌ Erro ao criar usuário:", error);
      
      let errorMessage = error.message;
      
      if (error.message.includes("Email rate limit")) {
        errorMessage = "Muitos emails enviados recentemente. Tente novamente em alguns minutos.";
      } else if (error.message.includes("Invalid email")) {
        errorMessage = "Email inválido. Verifique o formato do email.";
      } else if (error.message.includes("SMTP")) {
        errorMessage = "Erro no servidor de email. Verifique as configurações SMTP no Supabase.";
      }
      
      toast({
        title: "Erro ao criar usuário",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleResendInvite = async (userEmail: string) => {
    console.log("🔄 Reenviando convite para:", userEmail);
    
    toast({
      title: "Funcionalidade em desenvolvimento",
      description: "A opção de reenviar convite será implementada em breve.",
    });
  };

  const handleConfirmDelete = async () => {
    if (!currentUserEdit) return;
    
    setIsDeleting(true);
    console.log("🗑️ Excluindo usuário:", currentUserEdit.email);
    
    try {
      // Apenas excluir o perfil - o usuário precisará ser removido manualmente da autenticação
      const { error: profileError } = await supabase
        .from('profiles')
        .delete()
        .eq('id', currentUserEdit.id);

      if (profileError) throw profileError;

      await loadUsers();
      setDeleteDialogOpen(false);
      
      toast({
        title: "Usuário removido com sucesso",
        description: `${currentUserEdit.email} foi removido da organização.`
      });

      console.log("✅ Usuário excluído com sucesso");
    } catch (error: any) {
      console.error("❌ Erro ao excluir usuário:", error);
      toast({
        title: "Erro ao excluir usuário",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return {
    users,
    loading,
    sortColumn,
    sortDirection,
    filter,
    createDialogOpen,
    deleteDialogOpen,
    currentUserEdit,
    isSaving,
    isDeleting,
    setFilter,
    setCreateDialogOpen,
    handleSort,
    handleDelete,
    handleCreateUser,
    handleResendInvite,
    handleConfirmDelete
  };
}
